// --- START OF FILE knowledge-base-state.service.ts ---

import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { KnowledgeBaseItem, KBSearchResult } from '../../../shared/services/persistence-api.service'; // Adjust path
import { PersistedFileInfo } from './knowledge-base.component';

// Define the shape of our state
export interface KnowledgeBaseState {
  // Knowledge Base Items State
  knowledgeBaseItems: KnowledgeBaseItem[];
  originalKnowledgeBaseItems: KnowledgeBaseItem[]; // The full, unfiltered list
  searchResults: KBSearchResult[];
  isLoadingKB: boolean;
  errorKB: string | null;
  missingEmbeddingsExist: boolean;

  // Project Documents (Files) State
  persistedFiles: PersistedFileInfo[];
  isLoadingFiles: boolean;
  errorFiles: string | null;

  // Filtering & View State
  activeFileFilter: PersistedFileInfo | null;
  activeSourceTypeFilter: string; // 'all', 'document', etc.
  isSearchActive: boolean;
  searchQuery: string;
}

// Initial state for when the service is first created
const initialState: KnowledgeBaseState = {
  knowledgeBaseItems: [],
  originalKnowledgeBaseItems: [],
  searchResults: [],
  isLoadingKB: true,
  errorKB: null,
  missingEmbeddingsExist: false,

  persistedFiles: [],
  isLoadingFiles: true,
  errorFiles: null,

  activeFileFilter: null,
  activeSourceTypeFilter: 'all',
  isSearchActive: false,
  searchQuery: '',
};

@Injectable({
  providedIn: 'root'
})
export class KnowledgeBaseStateService {

  // The private, mutable state subject
  private readonly _state$ = new BehaviorSubject<KnowledgeBaseState>(initialState);

  // The public, read-only observable for components to subscribe to
  public readonly state$: Observable<KnowledgeBaseState> = this._state$.asObservable();

  constructor() { }

  // --- GETTERS ---
  // A convenience getter to get a snapshot of the current state value
  public get currentState(): KnowledgeBaseState {
    return this._state$.getValue();
  }

  // --- UPDATERS (MUTATORS) ---
  // These methods are how other parts of the app will change the state.
  // They take some data, create a new state object, and push it to the stream.

  // --- Document/File Updaters
  public setFilesLoading(): void {
    this._state$.next({ ...this.currentState, isLoadingFiles: true, errorFiles: null });
  }

  public setFiles(files: PersistedFileInfo[]): void {
    this._state$.next({ ...this.currentState, persistedFiles: files, isLoadingFiles: false });
  }

  public addUploadingFile(file: PersistedFileInfo): void {
    const currentFiles = this.currentState.persistedFiles;
    this._state$.next({ ...this.currentState, persistedFiles: [file, ...currentFiles] });
  }

  public updateUploadingFile(updatedFile: PersistedFileInfo): void {
    // Find and replace the file based on a transient property, like its name+timestamp, since it has no ID yet.
    // For simplicity, this example assumes the object reference is the same.
    const files = this.currentState.persistedFiles.map(f => f === updatedFile ? updatedFile : f);
    this._state$.next({ ...this.currentState, persistedFiles: files });
  }

  public removeFile(fileId: string): void {
    const updatedFiles = this.currentState.persistedFiles.filter(f => f.id !== fileId);
    this._state$.next({ ...this.currentState, persistedFiles: updatedFiles });
  }

  public setFilesError(error: string): void {
    this._state$.next({ ...this.currentState, errorFiles: error, isLoadingFiles: false, persistedFiles: [] });
  }

  // --- Knowledge Base Item Updaters
  public setKBLoading(): void {
    this._state$.next({ ...this.currentState, isLoadingKB: true, errorKB: null });
  }

  /** Sets the main, potentially filtered list of KIs to display */
  public setKnowledgeBaseItems(items: KnowledgeBaseItem[]): void {
    this._state$.next({ ...this.currentState, knowledgeBaseItems: items, isLoadingKB: false });
  }

  /** Sets the full, unfiltered list of KIs and updates the display list */
  public setOriginalKnowledgeBaseItems(items: KnowledgeBaseItem[]): void {
    const missingEmbeddings = items.some(item => item.embedding_model == null);
    this._state$.next({
      ...this.currentState,
      originalKnowledgeBaseItems: items,
      knowledgeBaseItems: items, // Initially, the displayed list is the full list
      missingEmbeddingsExist: missingEmbeddings,
      isLoadingKB: false
    });
  }

  public setKBError(error: string): void {
    this._state$.next({ ...this.currentState, errorKB: error, isLoadingKB: false, knowledgeBaseItems: [], originalKnowledgeBaseItems: [] });
  }

  // --- Filter & Search Updaters
  public setFileFilter(file: PersistedFileInfo | null): void {
    this._state$.next({
      ...this.currentState,
      activeFileFilter: file,
      isSearchActive: false, // File filter and search are mutually exclusive
      searchQuery: '',
      activeSourceTypeFilter: 'all' // Clear other filters
    });
  }

  public setSourceTypeFilter(sourceType: string): void {
    const originalItems = this.currentState.originalKnowledgeBaseItems;
    const filteredItems = sourceType === 'all'
      ? originalItems
      : originalItems.filter(item => item.source_type === sourceType);

    this._state$.next({
      ...this.currentState,
      activeSourceTypeFilter: sourceType,
      knowledgeBaseItems: filteredItems, // Update the displayed list based on the filter
      activeFileFilter: null, // Clear other filters
      isSearchActive: false,
      searchQuery: ''
    });
  }

  public setSearchState(query: string, results: KBSearchResult[]): void {
    const resultItems = results.map(r => r.item);
    this._state$.next({
      ...this.currentState,
      isSearchActive: true,
      searchQuery: query,
      searchResults: results,
      knowledgeBaseItems: resultItems,
      isLoadingKB: false,
      activeFileFilter: null, // Clear other filters
      activeSourceTypeFilter: 'all'
    });
  }

  public clearSearch(): void {
    this._state$.next({
      ...this.currentState,
      isSearchActive: false,
      searchQuery: '',
      searchResults: [],
      knowledgeBaseItems: [...this.currentState.originalKnowledgeBaseItems] // Revert to original list
    });
  }

  public resetAllFilters(): void {
    this._state$.next({
      ...this.currentState,
      isSearchActive: false,
      searchQuery: '',
      searchResults: [],
      activeFileFilter: null,
      activeSourceTypeFilter: 'all',
      knowledgeBaseItems: [...this.currentState.originalKnowledgeBaseItems]
    });
  }
}
